// EinstellungenView.tsx — #einstellungen: preferences ONLY (see the Daten/Einstellungen
// split: #daten is funds & data — fund list, coverage, refresh, source health; this view
// is everything else — Darstellung (theme), Standardwerte für neue Szenarien, and
// Konto & App). Nothing here reads or writes fund data.
//
// Standardwerte settings are applied only when a NEW scenario is created or a scenario
// slot is reset to defaults (see lib/scenario.ts createScenario, called from app.tsx's
// resetSlot) — changing a value here never rewrites a scenario already configured
// elsewhere. Darstellung (accentPreset) is the one field that DOES take effect immediately
// app-wide (app.tsx applies it via lib/theme.ts's applyAccentPreset on every change).
import { useState } from 'preact/hooks';
import { NumberField } from '../components/NumberField.js';
import { ToggleField } from '../components/ToggleField.js';
import { Hint, useExplain } from '../components/Hint.js';
import { postLogout } from '../lib/api.js';
import { defaultSettings, SETTINGS_BOUNDS } from '../lib/settings.js';
import type { AppSettings } from '../lib/settings.js';
import { ACCENT_PRESETS, ACCENT_PRESET_ORDER } from '../lib/theme.js';

export interface EinstellungenViewProps {
  settings: AppSettings;
  onSettingsChange: (settings: AppSettings) => void;
  onLoggedOut: () => void;
}

export function EinstellungenView({ settings, onSettingsChange, onLoggedOut }: EinstellungenViewProps) {
  const [loggingOut, setLoggingOut] = useState(false);
  const showExplanations = useExplain();
  const patch = (p: Partial<AppSettings>) => onSettingsChange({ ...settings, ...p });

  async function handleLogout() {
    setLoggingOut(true);
    try {
      await postLogout();
    } catch {
      // A failed logout call (e.g. the session had already expired server-side)
      // shouldn't leave the user stuck — drop to the login view regardless.
    } finally {
      setLoggingOut(false);
      onLoggedOut();
    }
  }

  return (
    <div>
      <div class="card">
        <h2>Darstellung</h2>
        <Hint>
          <p class="field-hint">Akzentfarbe der App wählen — gilt sofort für Buttons, Diagramme und Icons.</p>
        </Hint>
        <div class="field">
          <span class="field-label">Akzentfarbe</span>
          <div class="segmented segmented-scroll" role="group" aria-label="Akzentfarbe">
            {ACCENT_PRESET_ORDER.map((key) => {
              const def = ACCENT_PRESETS[key];
              return (
                <button
                  key={key}
                  type="button"
                  class="chip"
                  aria-pressed={settings.accentPreset === key}
                  onClick={() => patch({ accentPreset: key })}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      display: 'inline-block',
                      width: '0.75rem',
                      height: '0.75rem',
                      borderRadius: '50%',
                      background: def.accent,
                      marginRight: '0.4rem',
                      verticalAlign: 'middle',
                    }}
                  />
                  {def.label}
                </button>
              );
            })}
          </div>
        </div>

        <ToggleField
          label="Erklärungen anzeigen"
          checked={settings.showExplanations}
          onChange={(v) => patch({ showExplanations: v })}
          hint="Blendet Feld- und Abschnittserklärungen im ganzen Cockpit ein — dieselbe Umschaltung wie das ⓘ-Symbol in der Kopfzeile/Seitenleiste. Standardmäßig aus."
        />
      </div>

      <div class="card">
        <h2>Standardwerte für neue Szenarien</h2>
        <Hint>
          <p class="field-hint">
            Vorbelegung für ein neues Szenario und für „Auf Standard zurücksetzen" im
            Cockpit. Bereits konfigurierte Szenarien werden dadurch nicht verändert.
          </p>
        </Hint>

        <NumberField
          label="Rendite p.a."
          unit="%"
          value={settings.annualReturnPercent}
          onChange={(v) => patch({ annualReturnPercent: v })}
          min={SETTINGS_BOUNDS.annualReturnPercent.min}
          max={SETTINGS_BOUNDS.annualReturnPercent.max}
          step={0.1}
          sliderMax={30}
          sliderStep={0.5}
        />

        <NumberField
          label="Ertragsrendite (AGE)"
          unit="%"
          value={settings.incomeYieldPercent}
          onChange={(v) => patch({ incomeYieldPercent: v })}
          min={SETTINGS_BOUNDS.incomeYieldPercent.min}
          max={SETTINGS_BOUNDS.incomeYieldPercent.max}
          step={0.1}
        />

        <NumberField
          label="TER"
          unit="%"
          value={settings.terPercent}
          onChange={(v) => patch({ terPercent: v })}
          min={SETTINGS_BOUNDS.terPercent.min}
          max={SETTINGS_BOUNDS.terPercent.max}
          step={0.01}
        />

        <NumberField
          label="Einmalerlag"
          unit="€"
          value={settings.lumpSumEur}
          onChange={(v) => patch({ lumpSumEur: v })}
          min={SETTINGS_BOUNDS.lumpSumEur.min}
          max={SETTINGS_BOUNDS.lumpSumEur.max}
          step={100}
          sliderScale="money"
        />

        <NumberField
          label="Monatliche Rate"
          unit="€"
          value={settings.monthlyEur}
          onChange={(v) => patch({ monthlyEur: v })}
          min={SETTINGS_BOUNDS.monthlyEur.min}
          max={SETTINGS_BOUNDS.monthlyEur.max}
          step={10}
        />

        <NumberField
          label="Ausführungsgebühr"
          unit="€ / Ausführung"
          value={settings.executionFeeEur}
          onChange={(v) => patch({ executionFeeEur: v })}
          min={SETTINGS_BOUNDS.executionFeeEur.min}
          max={SETTINGS_BOUNDS.executionFeeEur.max}
          step={0.1}
        />

        <NumberField
          label="Sparplan-Dynamik (jährl. Erhöhung)"
          unit="%"
          value={settings.contributionStepUpPercent}
          onChange={(v) => patch({ contributionStepUpPercent: v })}
          min={SETTINGS_BOUNDS.contributionStepUpPercent.min}
          max={SETTINGS_BOUNDS.contributionStepUpPercent.max}
          step={0.1}
          sliderMax={10}
          sliderStep={0.5}
          hint={showExplanations ? 'Standard: 0 % (keine automatische Erhöhung).' : undefined}
        />

        <NumberField
          label="KESt-Satz"
          unit="%"
          value={settings.kestPercent}
          onChange={(v) => patch({ kestPercent: v })}
          min={SETTINGS_BOUNDS.kestPercent.min}
          max={SETTINGS_BOUNDS.kestPercent.max}
          step={0.1}
          sliderMax={55}
          sliderStep={0.5}
          hint={showExplanations ? 'Standard: 27,5 %.' : undefined}
        />

        <NumberField
          label="Inflationsrate (für Inflationsbereinigung)"
          unit="%"
          value={settings.inflationPercent}
          onChange={(v) => patch({ inflationPercent: v })}
          min={SETTINGS_BOUNDS.inflationPercent.min}
          max={SETTINGS_BOUNDS.inflationPercent.max}
          step={0.1}
          sliderMax={10}
          sliderStep={0.1}
          hint={
            showExplanations
              ? 'Standardwert für die Ergebnisansicht-Umschaltung „Inflationsbereinigt (heutige Kaufkraft)“. Beeinflusst nur die Anzeige, nie die berechneten Werte.'
              : undefined
          }
        />

        <div class="field">
          <span class="field-label">Ausschüttungsfrequenz</span>
          <div class="segmented" role="group" aria-label="Ausschüttungsfrequenz">
            <button
              type="button"
              class="chip"
              aria-pressed={settings.distributionFrequency === 'annual'}
              onClick={() => patch({ distributionFrequency: 'annual' })}
            >
              Jährlich
            </button>
            <button
              type="button"
              class="chip"
              aria-pressed={settings.distributionFrequency === 'quarterly'}
              onClick={() => patch({ distributionFrequency: 'quarterly' })}
            >
              Quartalsweise
            </button>
          </div>
          <Hint>
            <p class="field-hint">Nur für ausschüttende Fonds in der Prognose relevant.</p>
          </Hint>
        </div>

        <button
          type="button"
          class="btn btn-block"
          onClick={() =>
            onSettingsChange({
              ...defaultSettings(),
              accentPreset: settings.accentPreset,
              showExplanations: settings.showExplanations,
            })
          }
        >
          Auf Werkseinstellungen zurücksetzen
        </button>
        <Hint>
          <p class="field-hint">Setzt nur die Szenario-Standardwerte zurück, nicht die Darstellung.</p>
        </Hint>
      </div>

      <div class="card">
        <h2>Konto &amp; App</h2>
        <Hint>
          <p class="field-hint">Build-Stand dieser Installation und Abmelden.</p>
        </Hint>
        <button type="button" class="btn btn-block" onClick={handleLogout} disabled={loggingOut}>
          {loggingOut ? <span class="spinner" /> : 'Abmelden'}
        </button>
        <p class="build-stamp">Stand: {__BUILD_STAMP__}</p>
      </div>
    </div>
  );
}
